import React from 'react';
import { Box, Archive, Trash2, RefreshCw, Grid } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения связанных материалов и сохраненных проектов
 */
const RelatedMaterialsSidebar = ({ 
  formData, 
  projectType, 
  concreteGrade,
  savedProjects,
  deleteSavedProject,
  loadSavedProject
}) => {
  // Определение связанных материалов в зависимости от типа проекта и марки бетона
  const getRelatedMaterials = () => {
    // Базовые материалы для любого типа бетонных работ
    const baseMaterials = [
      { 
        name: 'Цемент', 
        amount: 310, 
        unit: 'кг', 
        description: 'На 1 м³ бетона марки M200'
      },
      { 
        name: 'Песок', 
        amount: 730, 
        unit: 'кг', 
        description: 'Очищенный, средней фракции'
      },
      { 
        name: 'Щебень', 
        amount: 1100, 
        unit: 'кг', 
        description: 'Фракция 5-20 мм'
      },
      { 
        name: 'Вода', 
        amount: 180, 
        unit: 'л', 
        description: 'Чистая, без примесей'
      }
    ];
    
    // Дополнительные материалы в зависимости от типа проекта
    const additionalMaterials = {
      foundation: [
        { 
          name: 'Гидроизоляция', 
          amount: 1.1, 
          unit: 'м²', 
          description: 'На 1 м² площади фундамента'
        },
        { 
          name: 'Арматура', 
          amount: 80, 
          unit: 'кг', 
          description: 'На 1 м³ бетона (средние значения)'
        }
      ],
      floor: [
        { 
          name: 'Армирующая сетка', 
          amount: 1.1, 
          unit: 'м²', 
          description: 'На 1 м² площади пола'
        },
        { 
          name: 'Демпферная лента', 
          amount: 0.4, 
          unit: 'м', 
          description: 'На 1 м² площади пола'
        }
      ],
      wall: [
        { 
          name: 'Опалубка', 
          amount: 2, 
          unit: 'м²', 
          description: 'На 1 м² площади стены (обе стороны)'
        },
        { 
          name: 'Арматура', 
          amount: 100, 
          unit: 'кг', 
          description: 'На 1 м³ бетона для стен'
        }
      ],
      column: [
        { 
          name: 'Опалубка круглая', 
          amount: 3.14, 
          unit: 'м²', 
          description: 'На 1 м высоты колонны ∅1м'
        },
        { 
          name: 'Вязальная проволока', 
          amount: 1.5, 
          unit: 'кг', 
          description: 'На 1 м³ бетона для колонн'
        }
      ],
      pool: [
        { 
          name: 'Гидроизоляция', 
          amount: 1.2, 
          unit: 'м²', 
          description: 'На 1 м² площади бассейна'
        },
        { 
          name: 'Пластификатор', 
          amount: 3, 
          unit: 'кг', 
          description: 'На 1 м³ бетона для бассейна'
        }
      ]
    };
    
    // Корректировка количества цемента в зависимости от марки бетона
    const cementAdjustments = {
      'M100': 0.8, // 80% от базового количества
      'M150': 0.9, // 90% от базового количества
      'M200': 1.0, // базовое количество
      'M250': 1.1, // 110% от базового количества
      'M300': 1.2, // 120% от базового количества
      'M350': 1.3, // 130% от базового количества
      'M400': 1.4  // 140% от базового количества
    };
    
    // Корректируем количество цемента в зависимости от марки бетона
    const adjustmentFactor = cementAdjustments[concreteGrade] || 1.0;
    const adjustedBaseMaterials = baseMaterials.map(material => {
      if (material.name === 'Цемент') {
        return {
          ...material,
          amount: Math.round(material.amount * adjustmentFactor),
          description: `На 1 м³ бетона марки ${concreteGrade}`
        };
      }
      return material;
    });
    
    // Объединяем базовые и дополнительные материалы
    return [...adjustedBaseMaterials, ...(additionalMaterials[projectType] || [])];
  };

  const relatedMaterials = getRelatedMaterials();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Box className="w-5 h-5 mr-2 text-amber-500" />
        Материалы и сохраненные проекты
      </h3>
      
      {/* Связанные материалы */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3">
          Необходимые материалы для {projectType === 'foundation' ? 'фундамента' : 
                                   projectType === 'floor' ? 'пола' : 
                                   projectType === 'wall' ? 'стен' : 
                                   projectType === 'column' ? 'колонн' : 
                                   projectType === 'pool' ? 'бассейна' : 
                                   'бетонной конструкции'}
        </h4>
        
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 overflow-hidden">
          <table className="min-w-full divide-y divide-amber-200 dark:divide-amber-700">
            <thead className="bg-amber-100 dark:bg-amber-800">
              <tr>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-amber-800 dark:text-amber-200 uppercase tracking-wider">
                  Материал
                </th>
                <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-amber-800 dark:text-amber-200 uppercase tracking-wider">
                  Количество
                </th>
              </tr>
            </thead>
            <tbody className="bg-amber-50 dark:bg-amber-900/10 divide-y divide-amber-100 dark:divide-amber-800">
              {relatedMaterials.map((material, index) => (
                <tr key={index} className="hover:bg-amber-100 dark:hover:bg-amber-800/30 transition-colors">
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <div className="font-medium text-amber-900 dark:text-amber-100">{material.name}</div>
                    <div className="text-xs text-amber-700 dark:text-amber-300">{material.description}</div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-amber-800 dark:text-amber-200">
                    {formatNumber(material.amount, 1)} {material.unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          * Указанные значения являются приблизительными и могут отличаться в зависимости от условий работы и качества материалов.
        </p>
      </div>

      {/* Сохраненные проекты */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">
            Сохраненные проекты
          </h4>
          {savedProjects.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {savedProjects.length} {savedProjects.length === 1 ? 'проект' : 
                                      savedProjects.length < 5 ? 'проекта' : 'проектов'}
            </span>
          )}
        </div>
        
        {savedProjects.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
            <Archive className="w-8 h-8 mx-auto text-gray-400 dark:text-gray-500 mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              У вас пока нет сохраненных проектов
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Рассчитайте объем бетона и нажмите "Сохранить проект"
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {savedProjects.map((project) => (
              <div 
                key={project.id} 
                className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                      {project.name}
                    </h5>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {project.results?.totalVolume?.toFixed(2) || 0} м³ – 
                      {project.results?.concreteGrade || concreteGrade}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {new Date(project.date).toLocaleDateString('ru-RU')}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-cyan-600 dark:text-gray-500 dark:hover:text-cyan-400 transition-colors p-1"
                      onClick={() => loadSavedProject(project.id)}
                      title="Загрузить проект"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 transition-colors p-1"
                      onClick={() => {
                        // Мы заменяем window.confirm на более безопасную проверку
                        if (window.confirm('Удалить этот проект?')) {
                          deleteSavedProject(project.id);
                        }
                      }}
                      title="Удалить проект"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {savedProjects.length > 0 && (
          <div className="mt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              fullWidth
              icon={<Grid className="w-4 h-4" />}
              onClick={() => {/* Здесь можно добавить переход на страницу со всеми проектами */}}
            >
              Показать все проекты
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RelatedMaterialsSidebar;